import { QueryFactory } from '@lmt/shared-types';
import { PasswordResetRequiredError } from '@lmt/unicorn-shared/app/api/auth/auth-client';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { APP_VERSION } from '@lmt/unicorn-shared/app/constants/constants';
import {
  getCurrentLanguage,
  useLanguages,
} from '@lmt/unicorn-shared/app/constants/languages';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { login } from '@lmt/unicorn-shared/app/features/auth/operations';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { UniStorageFactory } from '@lmt/unicorn-shared/app/helpers/uni-storage';
import {
  checkAppVersionCompatibility,
  devLabel,
  isDevVersion,
  isLocalDevelopmentVersion,
} from '@lmt/unicorn-shared/app/helpers/version-helper';
import { useEventCallback } from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Field } from '../common/Field';
import { SubmitButton } from '../common/ui-kit/buttons/SubmitButton';
import { IconButton } from '../common/ui-kit/IconButton';
import { Input } from '../common/ui-kit/inputs/Input';

import styles from './LoginForm.module.scss';
import unauthorizedWrapperStyles from './UnauthorizedWrapper.module.scss';
import LoginSettingsIcon from '@lmt/unicorn-shared/app/icons/login-settings.svg?react';
import LogoTextIcon from '@lmt/unicorn-shared/app/icons/logo-title.svg?react';
import { snakeCase } from 'lodash';
import { setLanguage } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { WindowControls } from '../toolbar/UniToolBar';
import { useModal } from '../common/ui-kit/modal/useModal';

const Logo = () => (
  <div className={styles.logo} data-test-id="main-logo">
    <LogoTextIcon className={styles.logoText} />
  </div>
);

export interface LoginFormData {
  login: string;
  password: string;
}

const nameof = nameofFactory<LoginFormData>();

type Props = {
  onSettingsIconClick?: () => void;
};

export const LoginForm: React.FunctionComponent<Props> = (props) => {
  // useEffect(() => {
  //   redirectToLoginPage().catch((e) => console.error('redirectToLoginPage', e));
  // }, []);
  // return null;
  const { register, handleSubmit, formState, setError } =
    useForm<LoginFormData>({
      shouldFocusError: true,
      criteriaMode: 'all',
    });
  const i18next = useScopedTranslation('login_form');
  const modals = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const loginHandler = useEventCallback(async (data: LoginFormData) => {
    try {
      await login(data.login, data.password);

      queryClient.resetQueries();
      UniStorageFactory.UniStorage.LastUserName = data.login;
    } catch (e: any) {
      if (e instanceof PasswordResetRequiredError) {
        navigate(
          pages.passwordReset.link({
            username: data.login,
            passwordResetToken: e.password_reset_token,
          }),
        );
        return;
      }
      const error = e.message ?? 'login_unknown_failure';
      setError('password', {
        type: 'validate',
        message: i18next.t(
          `auth_errors.${snakeCase(error)}` as any,
          i18next.t('auth_errors.login_unknown_failure'),
        ) as any,
      });
    }
  });

  const [isAppIncompatible, setIsAppIncompatible] = useState(false);

  /**
   * Check if desktop application version is compatible with backend or not
   */
  const checkAppCompatibility = useCallback(async () => {
    try {
      const apiVersion = await QueryFactory.VersionClient.version();
      if (
        apiVersion &&
        !checkAppVersionCompatibility(APP_VERSION, apiVersion)
      ) {
        setIsAppIncompatible(true);
        await modals.showError({
          text: i18next.t('app_version_incompatible_full_message', {
            appVersion: APP_VERSION,
            apiVersion: apiVersion,
          }),
        });
      }
    } catch (e) {
      console.log(`Error on app version check: ${e}`);
    }
  }, []);

  useEffect(() => {
    if (!isElectron() || isLocalDevelopmentVersion(APP_VERSION)) {
      return;
    }
    checkAppCompatibility();
  }, []);

  /**
   * User can create tenant when URL contains special parameter
   */
  const canCreateTenant = useMemo(() => {
    return location.search === '?wanna_create_tenant';
  }, [location.search]);

  const uiLanguages = useLanguages();
  const currentLanguage = getCurrentLanguage();
  return (
    <div className={unauthorizedWrapperStyles.mainContainer}>
      {isElectron() && !!WindowControls && (
        <div className={unauthorizedWrapperStyles.header}>
          <div className={styles.dragContainer}></div>
          <div className={styles.windowControls}>{WindowControls}</div>
        </div>
      )}
      <div className={unauthorizedWrapperStyles.mainSubcontainer}>
        <div
          // for desktop in splash screen the dev mode label sets up here:
          // apps/electron-main/public/splash-screen.html, styles and script
          className={clsx(styles.wrapper, {
            [styles.devMode]: isDevVersion(APP_VERSION),
          })}
          data-dev-version={devLabel}
          id="loginForm"
        >
          <div className={styles.toolbar}>
            {canCreateTenant && (
              <IconButton
                className={styles.toolbarIconButton}
                iconComponent={<LoginSettingsIcon />}
                onClick={() => navigate(pages.createTenant.link())}
              />
            )}
            {isElectron() && !!props.onSettingsIconClick && (
              <IconButton
                className={styles.toolbarIconButton}
                iconComponent={<LoginSettingsIcon />}
                onClick={props.onSettingsIconClick}
              />
            )}
          </div>
          <div className={styles.loginFormContainer}>
            <div className={styles.infoContainer}>
              <Logo />
              <div className={styles.text}>
                {i18next.t('copyright', {
                  currentYear: new Date().getFullYear(),
                })}
              </div>
              <div className={styles.text}>
                {i18next.t('app_version', {
                  version: APP_VERSION,
                })}
              </div>
              {isAppIncompatible && (
                <div className={styles.errorText}>
                  {i18next.t('app_version_incompatible_message')}
                </div>
              )}
              <DropDownInput
                useIdFunctionAsValue={true}
                rootClassName={styles.languageSwitcher}
                classes={{
                  option: styles.optionValue,
                  paper: styles.languageSwitcherCallout,
                }}
                options={uiLanguages}
                idFunction={'key'}
                getOptionLabel={'label'}
                required
                value={currentLanguage}
                variant={'formInput'}
                autosizeInputWidth={true}
                onValueChanged={(value) => {
                  if (value) {
                    setLanguage(value.key);
                  }
                }}
              />
            </div>
            <form
              className={styles.loginForm}
              onSubmit={handleSubmit(loginHandler)}
            >
              <Field
                className={styles.field}
                title={i18next.t('username')}
                titleClassName={styles.fieldTitle}
              >
                <Input
                  {...register(nameof('login'), requiredRule())}
                  defaultValue={UniStorageFactory.UniStorage.LastUserName}
                  variant={'normal'}
                  errorText={formState.errors.login?.message}
                  className={styles['input-error']}
                  errorClassname={styles['input-error-text']}
                />
              </Field>

              <Field
                className={styles.field}
                title={i18next.t('password')}
                titleClassName={styles.fieldTitle}
              >
                <Input
                  {...register(nameof('password'), requiredRule())}
                  errorText={formState.errors.password?.message}
                  variant={'normal'}
                  type="password"
                  className={styles['input-error']}
                  errorClassname={styles['input-error-text']}
                />
              </Field>
              <div className={styles.controls}>
                <SubmitButton
                  disabled={isAppIncompatible}
                  submitting={formState.isSubmitting}
                  className={styles.submit}
                  title={i18next.t('login_button')}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
